import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coffee, Cookie, Droplet, Sparkles } from 'lucide-react';

const Recipes = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const recipes = [];

  return (
    <div className="min-h-screen pb-24 pt-48">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-wide text-muted-foreground mb-4"></p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="recipes-headline">
            Ways to Enjoy Our Honey
          </h1>
          <p className="text-base md:text-lg text-foreground/70 max-w-3xl mx-auto leading-relaxed">
            From breakfast to skincare, honey is nature's most versatile ingredient. Here are some of our favorite ways to use it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/143c7995-c07b-4c81-a4f7-e0c409ad10c4/images/2f9fedebf266a15985296a4a7abf49789a9b471286933195ccf427738047063d.png"
              alt="Honey on Toast"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Classic Toast</h3>
                <p className="text-sm opacity-90 text-white">The simplest pleasures are often the best. Quality bread, butter and a generous drizzle of honey.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://images.unsplash.com/photo-1768039049614-f3c2bae3f1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwa2l0Y2hlbiUyMGNvdW50ZXJ8ZW58MHx8fHwxNzc1MDM3NDAyfDA&ixlib=rb-4.1.0&q=85"
              alt="Kitchen Counter"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Daily Ritual</h3>
                <p className="text-sm opacity-90 text-white">Make honey part of your morning routine. In tea, on yogurt, or straight from the jar.</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/mh8f46ty_Honey%20Latte%20Photography.jpg"
              alt="Honey Latte"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Honey Latte</h3>
                <p className="text-sm opacity-90 text-white">Swap sugar for a spoonful of honey in your morning coffee or latte for natural sweetness.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/kg3c4p4c_Oat%20Cookie%20Photography.webp"
              alt="Honey Oat Cookies"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Honey Oat Cookies</h3>
                <p className="text-sm opacity-90 text-white">Use honey as a natural binder and sweetener in your favorite cookie recipes. Wild Forest adds depth to baked goods.</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/yfnhl889_Spa%20Portrait%20Aesthetic.jpg"
              alt="Honey Face Mask"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Honey Face Mask</h3>
                <p className="text-sm opacity-90 text-white">Mix honey with yogurt for a natural, moisturizing face mask. Leave on for 15 minutes. Raw honey has natural antibacterial properties.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative overflow-hidden rounded-2xl md:rounded-[2rem] aspect-[4/3]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/roq2p083_Roasted%20Vegetables%20Edit.jpg"
              alt="Honey Glazed Vegetables"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>Honey Glazed Vegetables</h3>
                <p className="text-sm opacity-90 text-white">Drizzle over roasted carrots, pumpkin or sweet potato for a beautiful caramelized finish. Wild Forest pairs perfectly with savory dishes.</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          {recipes.map((recipe, index) => {
            const Icon = recipe.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-muted/30 border border-border hover:border-primary/30 transition-all duration-300"
                data-testid={`recipe-${index}`}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-6">
                  <Icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold text-foreground mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {recipe.title}
                </h3>
                <p className="text-foreground/70 leading-relaxed mb-4">
                  {recipe.description}
                </p>
                <p className="text-sm text-primary font-medium">
                  Tip: {recipe.tip}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Recipes;
